import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { freezeScroll, releaseScroll } from '../utils/scroll';
import { animate } from '../utils/animate';
import { FocusTrap } from '../utils/focus-trap';
// Check for seperate modal root
let root;
if (typeof document !== 'undefined') {
    root = document.getElementById('tx-modal-root');
}
/**
 * Accessible Modal that can either be displayed using a portal or inline.
 * To control where a modal is rendered include an HTMLElement with the id "tx-modal-root" somewhere in your document.
 * If no portal element is found the modal will simply render where the component has been called.
 */
export function TxModal({ children, open = false, onClose, onOpen, focusOnFirst = true, className, closeOnEsc = true, showCloseBtn = false, }) {
    const modalRef = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const extraClass = className ? ` ${className}` : '';
    const [ogFocusEl, setOgFocusEl] = useState();
    /**
     * When the "open" prop changes handle opening and closing the modal with animations
     */
    useEffect(() => {
        if (open && !isOpen) {
            setOgFocusEl(document.activeElement);
            setOpen(open);
            if (document.body.clientHeight > window.innerHeight) {
                freezeScroll();
            }
            if (onOpen) {
                onOpen();
            }
        }
        else if (!open && isOpen) {
            close();
        }
    }, [open]);
    /**
     * When "isOpen" state changes enable the focus trap
     */
    useEffect(() => {
        if (!modalRef.current) {
            return () => { };
        }
        const focusTrap = new FocusTrap(modalRef.current);
        focusTrap.start();
        if (focusOnFirst) {
            if (focusTrap.firstFocusableEl) {
                focusTrap.firstFocusableEl.focus();
            }
            else {
                modalRef.current.focus();
            }
        }
        return () => {
            focusTrap.stop();
        };
    }, [isOpen]);
    /**
     * Listen for escape key
     */
    useEffect(() => {
        function onKeyUp(e) {
            if (closeOnEsc && e.key && e.key.toUpperCase() === 'ESCAPE') {
                close();
            }
        }
        document.addEventListener('keyup', onKeyUp);
        return () => {
            document.removeEventListener('keyup', onKeyUp);
        };
    }, [ogFocusEl]);
    function close() {
        // refocus on previously focused element
        if (ogFocusEl) {
            ogFocusEl.focus();
        }
        animate(modalRef.current, 'tx-modal--exit').then(() => {
            setOpen(false);
            releaseScroll();
            if (onClose) {
                onClose();
            }
        });
    }
    function modalContent() {
        return isOpen ? (React.createElement("div", { tabIndex: -1, className: 'tx-modal tx-modal--enter' + extraClass, ref: modalRef },
            React.createElement("div", { className: "tx-modal__content" },
                showCloseBtn && (React.createElement("button", { type: "button", className: "tx-modal__content__close", onClick: close, "data-focus-trap-skip": true }, "\u2715")),
                children),
            React.createElement("div", { className: "tx-modal__overlay" }))) : null;
    }
    return root ? createPortal(modalContent(), root) : modalContent();
}
export function TxModalHeader({ children }) {
    return React.createElement("div", { className: "tx-modal-header" }, children);
}
export const TxModalBody = forwardRef(({ children, contain = true }, fr) => {
    const ref = useRef(null);
    useEffect(() => {
        // find the closest modal
        const modal = ref.current.closest('.tx-modal__content');
        if (!modal) {
            throw new Error('TxModalBody must be placed inside of TxModal');
        }
        // manually forward ref
        if (fr) {
            if (typeof fr === 'function') {
                fr(ref.current);
            }
            else {
                fr.current = ref.current;
            }
        }
        // force element height to auto on init
        ref.current.style.height = 'auto';
        if (contain) {
            // calculate the height
            ref.current.style.height = calculateModalBodyHeight(modal) + 'px';
        }
    }, [children]);
    return (React.createElement("div", { tabIndex: 0, className: "tx-modal-body", ref: ref }, children));
});
export function TxModalFooter({ children }) {
    return React.createElement("div", { className: "tx-modal-footer" }, children);
}
export function calculateModalBodyHeight(modal) {
    let height = modal.clientHeight;
    const header = modal.querySelector('.tx-modal-header');
    if (header) {
        height = height - header.clientHeight;
    }
    const footer = modal.querySelector('.tx-modal-footer');
    if (footer) {
        height = height - footer.clientHeight;
    }
    const comp = getComputedStyle(modal);
    height = height - parseFloat(comp.paddingTop || '0') - parseFloat(comp.paddingBottom || '0');
    return height;
}
//# sourceMappingURL=Modal.js.map